// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — the three modes.
//
// A mode has to change how the game PLAYS, not just a number. This game has exactly one thing worth
// turning, and it is the SUPPLY CLOCK — so all three modes move it on a different axis and none of
// them touches a tariff value, a card count or a bounty:
//
//   LONG HAUL   nothing changes            The clock is entirely player-driven: a tariff falls only
//                                          because somebody sold into it. An uncontested ware holds
//                                          its price for ever, so patience is free on the wares your
//                                          rival is ignoring and lethal on the one they are building.
//                                          The whole game is reading which column they are walking
//                                          toward.
//
//   BRISK       an exogenous BELL burns    WHEN value dies. Copper's 8 is gone by pair three whether
//               the dearest live chit      you were ready or not, so there is no safe hold at all and
//               every turn-pair            the correct play collapses toward early two-card sales on
//                                          whatever is about to die. Tier III bounties (20+ chits in
//                                          one sale) become nearly unreachable late.
//
//   DEARTH      the trestle refills ONLY   WHEN supply arrives. A continuous stream becomes a
//               at the start of a turn     countable block, and PARITY becomes a resource: the
//               that finds it empty        trestle deltas are TAKE -1, HIRE -(porters), BARTER -k and
//                                          SELL 0, so SELL is the only row-neutral move — the "pass"
//                                          in a standoff, except passing costs real cards and drains
//                                          a tariff. Emptying the trestle hands your rival first pick
//                                          of five fresh cards, so taking a card you do not want is a
//                                          legitimate tempo play, and leaving porters parked there
//                                          BLOCKS the refill, which turns HIRE from a chore into a
//                                          scalpel. In Long Haul you count sets; here you count plies.
//
// The HOST's pick travels frozen inside rematch.ts's round start. `modeOf` guards an id off the wire
// with Object.hasOwn — a bare `MODES[id] || DEFAULT` lets 'constructor' through as a Mode of
// undefined fields, which reaches the dealer as `undefined` and deals a game nobody can play.

export type ModeId = 'longhaul' | 'brisk' | 'dearth';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Brisk only: an exogenous chit burn at the end of every turn-pair from pair 2. */
  readonly bell: boolean;
  /**
   * Long Haul / Brisk refill the trestle after every TAKE, HIRE and BARTER. Dearth refills only at
   * the start of a turn that finds the trestle completely empty.
   */
  readonly refill: 'always' | 'onEmpty';
}

export const MODES: Record<ModeId, Mode> = {
  longhaul: {
    id: 'longhaul',
    name: 'Long Haul',
    blurb:
      'The straight game. A price falls only because somebody sold into it — so patience is free on a ware nobody wants, and ruinous on the one they are quietly collecting.',
    bell: false,
    refill: 'always',
  },
  brisk: {
    id: 'brisk',
    name: 'Brisk',
    blurb:
      'A bell rings every turn-pair and burns the dearest chit left on the table. Nobody gets it. There is no safe hold — the top of every column is on a timer you cannot stop.',
    bell: true,
    refill: 'always',
  },
  dearth: {
    id: 'dearth',
    name: 'Dearth',
    blurb:
      'The trestle is refilled only once it is picked completely bare. Supply arrives in blocks, so who empties it decides who gets first pick of the next five — and a parked porter blocks the whole delivery.',
    bell: false,
    refill: 'onEmpty',
  },
};

export const MODE_IDS: readonly ModeId[] = ['longhaul', 'brisk', 'dearth'];
export const DEFAULT_MODE: ModeId = 'longhaul';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id as ModeId];
  return MODES[DEFAULT_MODE];
}

/**
 * THE BELL CADENCE IS THREE PLIES, AND THE 3 IS LOAD-BEARING — pin it with a test.
 *
 * The obvious cadence is "once per turn-pair", and it is measurably unfair: with a period of 2 and
 * two seats, the SAME player always acts immediately before every bell and the same player always
 * acts immediately after one. Measured over three independent seed families at n=50 each, that was
 * worth roughly 2 chits and 6 percentage points of win rate to the second mover, in the same
 * direction every time (mover win rate 44% / 34% / 44%).
 *
 * A period COPRIME to the number of seats rotates who is standing next to the bell, and 3 is the
 * smallest such period. It is the same shape as a well-known fix in this fleet: an even ramp period
 * silently restored a first-player edge that an odd one cancelled for free.
 */
export const BELL_PERIOD = 3;
export const FIRST_BELL_PLY = 3;
